import React, { useRef } from 'react';

// Styles & Assets
import styles from './FormFrequency.module.css';

// Services
import { getDaysWeek, getFrequenciesCustom } from '../../../../services/getPollAttribute';

// Hooks
import { useState } from 'react';

// Components
import CircleSelector from '../../CircleSelector/CircleSelector';
import CheckboxButton from '../../CheckboxButton/CheckboxButton';

// Interfaces
import { PollFrequency, CustomFrequency, WeekDayNumber } from '../../../../interfaces/Poll';

// Other
import { DEFAULT_ALL_WEEK_DAYS } from '../../../../data/ConstantsPolls';

// Language
import { useTranslation } from 'react-i18next';

interface Props {
    selectedFreq: CustomFrequency,
    handleClickFrequency: (freq: CustomFrequency) => void
    temporaryFreq: PollFrequency
    setTemporaryFreq: React.Dispatch<React.SetStateAction<PollFrequency>>
}

interface PropsFrequenciesCustom {
    temporaryFreq: PollFrequency,
    setTemporaryFreq: React.Dispatch<React.SetStateAction<PollFrequency>>
}

const FormFrequency: React.FC<Props> = ({ selectedFreq, handleClickFrequency, temporaryFreq, setTemporaryFreq }) => {

    const { t } = useTranslation();

    const getCustomFrequencyComponent = () => {
        if (selectedFreq === 'daily') return <DailyFrequencyCustom temporaryFreq={temporaryFreq} setTemporaryFreq={setTemporaryFreq} />;
        else if (selectedFreq === 'weekly') return <WeeklyFrequencyCustom temporaryFreq={temporaryFreq} setTemporaryFreq={setTemporaryFreq} />;
        else if (selectedFreq === 'monthly') return <MonthlyFrequencyCustom temporaryFreq={temporaryFreq} setTemporaryFreq={setTemporaryFreq} />;
        // else if (selectedFreq === 'annual') return <AnnualFrequencyCustom setTemporaryFreq={setTemporaryFreq} />;
    };

    return (
        <>
            <div className={styles.frequencyContainer}>
                <h3 className={styles.subtitle}>{t('frequency')}</h3>
                <div className={styles.frequencyOptions}>
                    {getFrequenciesCustom().map(freqLabel =>
                        <CircleSelector
                            key={freqLabel}
                            text={t(freqLabel)}
                            active={selectedFreq === freqLabel}
                            handleClick={() => handleClickFrequency(freqLabel)}
                        />
                    )}
                </div>
            </div>
            <div className={styles.customFrequencyContainer}>
                {getCustomFrequencyComponent()}
            </div>
        </>
    );
};

export default FormFrequency;

const DailyFrequencyCustom: React.FC<PropsFrequenciesCustom> = ({ temporaryFreq, setTemporaryFreq }) => {

    const { t } = useTranslation();

    const [option, setOption] = useState(temporaryFreq.frecuency !== null ? 0 : 1);

    const numDaysInputRef = useRef<HTMLInputElement>(null);

    const handleClickFirstOption = () => {
        setOption(0);
        updateFreq();
    };

    const updateFreq = () => {
        if (numDaysInputRef.current?.value)
            setTemporaryFreq({
                frecuency: Number(numDaysInputRef.current?.value),
                monthly_frequency_day: null,
                semanal_frequency: null,
                frequency_duration: null
            });
    };

    const handleClickWeekDays = () => {
        setTemporaryFreq(DEFAULT_ALL_WEEK_DAYS);
        setOption(1);
    };

    return (
        <>
            <div className={styles.option}>
                <CircleSelector
                    active={option === 0}
                    text={t('every')}
                    handleClick={handleClickFirstOption}
                />
                <input ref={numDaysInputRef} onChange={() => updateFreq()} className={styles.inputNumber} type='number' min={1} max={7} defaultValue={temporaryFreq.frecuency || 1} disabled={option === 1}></input>
                <span>{t('days')}</span>
            </div>
            <CircleSelector
                active={option === 1}
                text={t('every_working_day_week')}
                handleClick={handleClickWeekDays}
            />
        </>
    );
};

const WeeklyFrequencyCustom: React.FC<PropsFrequenciesCustom> = ({ temporaryFreq, setTemporaryFreq }) => {

    const { t } = useTranslation();

    const handleChangeNumWeeks = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setTemporaryFreq(oldFreq => {
            const newFreq = { ...oldFreq };
            return {
                ...newFreq,
                frequency_duration: Number(evt.target.value)
            };
        });
    };

    const handleClickDay = (dayIndex: WeekDayNumber, checked: boolean) => {
        setTemporaryFreq(oldFreq => {
            const newFreq = { ...oldFreq };
            return {
                frecuency: null,
                monthly_frequency_day: null,
                semanal_frequency: checked ?
                    [...newFreq.semanal_frequency || [], dayIndex] :
                    newFreq.semanal_frequency?.filter(day => day !== dayIndex),
                frequency_duration: newFreq.frequency_duration
            };
        });

    };

    return (
        <>
            <div className={styles.option}>
                <span className={styles.semiBold}>{t('repeat_every')}</span>
                <input onChange={handleChangeNumWeeks} className={styles.inputNumber} type='number' min={1} max={52} defaultValue={temporaryFreq.frequency_duration || 1}></input>
                <span className={styles.semiBold}>{t('weeks_on')}:</span>
            </div>
            <div className={styles.weekContainerGrid}>
                {getDaysWeek().map(day =>
                    <CheckboxButton
                        key={day.id}
                        size={11}
                        label={t(day.label)}
                        defaultChecked={temporaryFreq.semanal_frequency?.includes(day.id)}
                        onClick={(checked) => handleClickDay(day.id, checked)}
                    />
                )}
            </div>
        </>
    );
};

const MonthlyFrequencyCustom: React.FC<PropsFrequenciesCustom> = ({ temporaryFreq, setTemporaryFreq }) => {

    const { t } = useTranslation();

    const handleChangeDay = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setTemporaryFreq(oldFreq => {
            const newFreq = { ...oldFreq };
            return {
                ...newFreq,
                monthly_frequency_day: Number(evt.target.value)
            };
        });
    };

    const handleChangeMonth = (evt: React.ChangeEvent<HTMLInputElement>) => {
        setTemporaryFreq(oldFreq => {
            const newFreq = { ...oldFreq };
            return {
                ...newFreq,
                frequency_duration: Number(evt.target.value)
            };
        });
    };

    return (
        <>
            <div className={styles.option}>
                <span>{t('on_day')}</span>
                <input onChange={handleChangeDay} className={styles.inputNumber} type='number' min={1} max={31} defaultValue={temporaryFreq.monthly_frequency_day || 1} disabled={false}></input>
                <span>{t('of_every')}</span>
                <input onChange={handleChangeMonth} className={styles.inputNumber} type='number' min={1} max={12} defaultValue={temporaryFreq.frequency_duration || 1} disabled={false}></input>
                <span>{t('months')}</span>
            </div>
        </>
    );
};

// const AnnualFrequencyCustom: React.FC<{ setTemporaryFreq: React.Dispatch<React.SetStateAction<PollFrequency>> }> = ({ setTemporaryFreq }) => {

//     const { t } = useTranslation();

//     const handleChangeFreqDuration = (evt: React.ChangeEvent<HTMLInputElement>) => {
//         setTemporaryFreq(oldFreq => {
//             const newFreq = { ...oldFreq };
//             return {
//                 ...newFreq,
//                 frequency_duration: Number(evt.target.value) * 12
//             };
//         });
//     };

//     const handleChangeDay = (evt: React.ChangeEvent<HTMLInputElement>) => {
//         setTemporaryFreq(oldFreq => {
//             const newFreq = { ...oldFreq };
//             return {
//                 ...newFreq,
//                 monthly_frequency_day: Number(evt.target.value)
//             };
//         });
//     };


//     const handleChangeMonth = (evt: React.ChangeEvent<HTMLInputElement>) => {
//         setTemporaryFreq(oldFreq => {
//             const newFreq = { ...oldFreq };
//             return {
//                 ...newFreq,
//                 frequency_duration: Number(evt.target.value)
//             };
//         });
//     };

//     return (
//         <>
//             <div className={styles.option}>
//                 <span>{t('repeat_every')}</span>
//                 <input onChange={handleChangeFreqDuration} className={styles.inputNumber} type='number' min={1} max={52} defaultValue={1}></input>
//                 <span>{t('years_on')}:</span>
//             </div>
//             <div className={styles.option}>
//                 <input onChange={handleChangeDay} className={styles.inputNumber} type='number' min={1} max={31} defaultValue={1} disabled={false}></input>
//                 <span>{t('of')}</span>
//                 <select className={`${styles.inputNumber} ${styles.select}`}>
//                     {getMonthsYear().map(month => <option key={month.id}>{month.label}</option>)}
//                 </select>
//             </div>
//         </>
//     );
// };
